// ---------------- Component for the header of the application --------------------
// ----------------------------------------------------------------------------------

// import necessary libraries and hooks
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useLatestPrice } from '../hooks/useStockData';
import { useFullscreen } from '../contexts/FullscreenContext';
import './Header.css'; // import the styling for the header

// create the header component
const Header = ({ stockData, timeframe, onTimeframeChange }) => { // give it stockdata, timeframe and function to change timeframe

  // set all constants needed with defaults
  const symbol = stockData?.symbol || 'AAPL'; // default to AAPL if no stock data
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('2023-01-01');
  const [customEndDate, setCustomEndDate] = useState('2024-12-31');

  // fetch latest price every 60 seconds from the useLatestPrice Hook which calls getLatestPrice from API service
  const { latestPrice } = useLatestPrice(symbol, 60000); 
  const { setHeaderHeight } = useFullscreen();
  const headerRef = useRef(null); 

  // use effect to set the header height in the fullscreen context when the component mounts or when the header height changes
  useEffect(() => {
    if (headerRef.current && setHeaderHeight) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [setHeaderHeight]);

  // define the years for the timeframe selection and the full range for the "All" button
  const years = ['2015','2016','2017','2018','2019','2020','2021','2022','2023','2024','2025'];
  const fullRange = '2015-01-01|2025-12-31';

  const priceData = latestPrice; // use latest price if available, else use stock data

  const handleCustomDateApply = () => { // function for the custom date range picker to be applied
    onTimeframeChange(`${customStartDate}|${customEndDate}`);
    setShowCustomPicker(false);
  };

  // determine if the current timeframe is a custom range that doesn't match the predefined year options, and create a label for it
  // check if it is present and includes a '|' character and that the first part of the timeframe is not in the years array
  const isCustomTimeframe = timeframe && timeframe.includes('|') && !years.includes(timeframe.split('|')[0]?.substring(0,4));

  let displayCustomLabel; // declare the variable to hold the label for the custom button

  if (isCustomTimeframe) {
    // pull apart the timeframe string into a start date and end date
    const parts = timeframe.split('|');   
    const startDate = parts[0];           
    const endDate = parts[1];             

    displayCustomLabel = startDate + ' → ' + endDate; // create a label for the custom button in the format "start date → end date"
  } else {

    displayCustomLabel = 'Custom';
  }

  return ( // return the JSX for the header component

    <div className="header" ref={headerRef}> {/* main header container with a ref to measure its height for fullscreen mode */}

      <div className="stock-header"> {/* section for displaying the stock symbol and price information */}

        <div className="header-left">

          <h1>{symbol}</h1> {/* display the stock symbol */}

          <div className="stock-metrics">

            <span className="current-price">${priceData?.price?.toFixed(2) || '0.00'}</span> {/* display the current price of the stock */}
            <span className={`price-change ${(priceData?.change || 0) >= 0 ? 'positive' : 'negative'}`}> {/* display the price change with color coding */}
              {(priceData?.change || 0) >= 0 ? '+' : ''}{priceData?.change?.toFixed(2) || '0.00'}  {/* display the price change with color coding */}
              ({priceData?.changePercent?.toFixed(2) || '0.00'}%) {/* display the price change and percentage with color coding */}
            </span>

          </div>
        </div>

        {priceData?.dataTimestamp && ( // show the data timestamp if it exists 
          <div className="data-timestamp">
            <span className="timestamp-label">Data as of:</span>
            <span className="timestamp-value">{priceData.dataTimestamp}</span>
          </div>
        )}
      </div>


      {/* Timeframe selection section with buttons for each year, an "All" button for the full range, and a custom date range picker */}
      <div className="timeframe-selector"> {/* timeframe selection buttons */}
        <div className="timeframe-group">
          <label className="timeframe-label">Year:</label>

          {/* All button — full 2015–2025 range */}
          <button
            className={timeframe === fullRange ? 'active' : ''}
            onClick={() => onTimeframeChange(fullRange)}
          >
            All
          </button>

          {years.map(yr => ( // loop through the years array to create a button for each one
            <button
              key={yr}
              className={timeframe === yr ? 'active' : ''}
              onClick={() => onTimeframeChange(yr)}
            >
              {yr}
            </button>
          ))}

          {/* Custom Date Range Button */}
          <div className="custom-date-container">
            <button
              className={`custom-date-btn ${isCustomTimeframe ? 'active' : ''}`}
              onClick={() => setShowCustomPicker(!showCustomPicker)} // toggle the custom date picker when the button is clicked
            > 
              <Calendar size={14} />  {/* calendar icon for the custom date button */}
              {isCustomTimeframe ? displayCustomLabel : 'Custom'}
              <ChevronDown size={14} className={showCustomPicker ? 'rotated' : ''} />
            </button>
            
            {showCustomPicker && ( // show the custom date picker if the custom date button is clicked
              <div className="custom-date-picker">
                <div className="date-input-group">
                  <label>Start Date:</label>
                  <input // input for the start date of the custom range
                    type="date"
                    value={customStartDate}
                    min="2015-01-01"
                    max="2025-12-31"
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>

                <div className="date-input-group">
                  <label>End Date:</label>
                  <input // input for the end date of the custom range
                    type="date"
                    value={customEndDate}
                    min="2015-01-01"
                    max="2025-12-31"
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
                
                <div className="date-picker-actions">
                  <button  // apply button for the custom date range picker 
                    className="apply-btn"
                    onClick={handleCustomDateApply}
                    disabled={customStartDate >= customEndDate}
                  >
                    Apply
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={() => setShowCustomPicker(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header; // export the header component
